import { useEffect, useState } from 'react';

function getSavedValue<T>(key: string, initialValue: T): T {
  const savedValue = localStorage.getItem(key);
  if (savedValue != null) {
    try {
      return JSON.parse(savedValue);
    } catch {
      // biome-ignore lint/suspicious/noConsole: <debug>
      console.error('Error reading JSON from LocalStorage');
    }
  }
  return initialValue;
}

const STORAGE_EVENT_PREFIX = 'local-storage-update:';

const useLocalStorage = <T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((val: T) => T)) => void] => {
  const [value, setValue] = useState<T>(() => getSavedValue(key, initialValue));

  useEffect(() => {
    const handleStorageChange = (e: CustomEvent<{ value: T }>) => {
      setValue(e.detail.value);
    };

    window.addEventListener(`${STORAGE_EVENT_PREFIX}${key}`, handleStorageChange as EventListener);

    const handleBrowserStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        try {
          setValue(JSON.parse(e.newValue));
        } catch {
          // biome-ignore lint/suspicious/noConsole: <debug>
          console.error('Error parsing storage event value');
        }
      }
    };

    window.addEventListener('storage', handleBrowserStorageChange);

    return () => {
      window.removeEventListener(
        `${STORAGE_EVENT_PREFIX}${key}`,
        handleStorageChange as EventListener,
      );
      window.removeEventListener('storage', handleBrowserStorageChange);
    };
  }, [key]);

  const updateValue = (newValue: T | ((val: T) => T)) => {
    setValue((prevValue) => {
      const nextValue = newValue instanceof Function ? newValue(prevValue) : newValue;

      localStorage.setItem(key, JSON.stringify(nextValue));

      window.dispatchEvent(
        new CustomEvent(`${STORAGE_EVENT_PREFIX}${key}`, {
          detail: { value: nextValue },
        }),
      );

      return nextValue;
    });
  };

  return [value, updateValue];
};

export default useLocalStorage;
